import { attrValue, erasedAttrClass } from '@/entity/attrValue';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Empty, Space, Tabs, Tag, Typography } from 'antd';
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  DeploymentUnitOutlined,
  FieldTimeOutlined,
  HistoryOutlined,
  InboxOutlined,
  MailOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { getEntity360, getEntityEvidence, getEntityTimeline, getEntityUbo } from '@/api/entities';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type {
  ClockView,
  Entity360,
  EntityTimeline as EntityTimelineData,
  EvidenceView,
  TimelineEvent,
  TimelineKind,
  UboResult,
} from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { usePageTitle } from '@/components/pageTitle';
import { EntityStatusChip, RiskChip } from '@/entity/chips';
import { EvidenceList } from '@/entity/EvidenceList';
import { UboTree } from '@/entity/UboTree';
import { EntityTimeline } from '@/entity/EntityTimeline';
import { RfiListPanel } from '@/rfi/RfiListPanel';
import { formatAbsolute, formatRelative } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;
const { CheckableTag } = Tag;

const TIMELINE_KINDS: TimelineKind[] = [
  'GRAPH_CHANGE',
  'EVIDENCE_ADDED',
  'RUN_STARTED',
  'RUN_COMPLETED',
  'DECISION_RECORDED',
  'SIGNAL_RECEIVED',
  'SIGNAL_RESOLVED',
  'CLOCK_EVENT',
  'WORK_ITEM',
  'RFI',
  'DOCUMENT_OPENED',
];

function isoToLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function localInputToIso(v: string): string | null {
  if (!v) return null;
  const t = Date.parse(v);
  if (Number.isNaN(t)) return null;
  return new Date(t).toISOString();
}

function ClocksPanel({ clocks, now }: { clocks: ClockView[]; now: number }) {
  if (!clocks || clocks.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lifecycle clocks." />;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {clocks.map((c) => (
        <div className="ol-card" key={c.id} style={{ minWidth: 190, flex: '1 1 190px', padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 12.5 }}>{c.typeKey}</span>
            <Tag bordered={false}>{c.state}</Tag>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', marginTop: 5 }}>
            {c.riskBand ? `${c.riskBand} · ` : ''}
            {c.cycle != null ? `cycle ${c.cycle}` : ''}
          </div>
          {c.dueAt && (
            <div style={{ fontSize: 11.5, marginTop: 3, color: 'var(--ol-text-tertiary)' }}>
              <ClockCircleOutlined /> due {formatRelative(c.dueAt, now)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function EntityDetailPage() {
  const { entityId = '' } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  usePageTitle(useMemo(() => `Entity ${entityId}`, [entityId]));

  const asOf = searchParams.get('asOf') ?? undefined;
  const [pending, setPending] = useState(isoToLocalInput(asOf));
  const [tab, setTab] = useState('overview');
  const [now, setNow] = useState(() => Date.now());

  const [entity, setEntity] = useState<Entity360 | null>(null);
  const [ubo, setUbo] = useState<UboResult | null>(null);
  const [evidence, setEvidence] = useState<EvidenceView[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [kinds, setKinds] = useState<TimelineKind[]>([]);

  const [loadingCore, setLoadingCore] = useState(false);
  const [entityUnavailable, setEntityUnavailable] = useState(false);
  const [uboUnavailable, setUboUnavailable] = useState(false);
  const [evidenceUnavailable, setEvidenceUnavailable] = useState(false);
  const [timelineUnavailable, setTimelineUnavailable] = useState(false);

  useEffect(() => {
    setPending(isoToLocalInput(asOf));
  }, [asOf]);

  const loadCore = useCallback(async () => {
    if (!entityId) return;
    setLoadingCore(true);
    setNow(Date.now());
    const [e, u, ev] = await Promise.allSettled([
      getEntity360(entityId, asOf),
      getEntityUbo(entityId, { asOf }),
      getEntityEvidence(entityId, asOf),
    ]);
    if (e.status === 'fulfilled') {
      setEntity(e.value);
      setEntityUnavailable(false);
    } else {
      const n = normalizeApiError(e.reason);
      setEntity(null);
      if (isNotAvailable(n)) setEntityUnavailable(true);
      else message.error(n.message);
    }
    if (u.status === 'fulfilled') {
      setUbo(u.value);
      setUboUnavailable(false);
    } else {
      setUbo(null);
      setUboUnavailable(isNotAvailable(normalizeApiError(u.reason)));
    }
    if (ev.status === 'fulfilled') {
      setEvidence(ev.value);
      setEvidenceUnavailable(false);
    } else {
      setEvidence([]);
      setEvidenceUnavailable(isNotAvailable(normalizeApiError(ev.reason)));
    }
    setLoadingCore(false);
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, asOf]);

  const loadTimeline = useCallback(async () => {
    if (!entityId) return;
    try {
      const t: EntityTimelineData = await getEntityTimeline(entityId, {
        to: asOf,
        kinds: kinds.length ? kinds : undefined,
        pageSize: 200,
      });
      setTimeline(t.events);
      setTimelineUnavailable(false);
    } catch (e) {
      const n = normalizeApiError(e);
      setTimeline([]);
      if (isNotAvailable(n)) setTimelineUnavailable(true);
      else message.error(n.message);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, asOf, kinds]);

  useEffect(() => {
    void loadCore();
  }, [loadCore]);
  useEffect(() => {
    void loadTimeline();
  }, [loadTimeline]);

  const applyAsOf = useCallback(() => {
    const iso = localInputToIso(pending);
    if (iso) {
      searchParams.set('asOf', iso);
      setSearchParams(searchParams, { replace: false });
    }
  }, [pending, searchParams, setSearchParams]);

  const returnToNow = useCallback(() => {
    searchParams.delete('asOf');
    setSearchParams(searchParams, { replace: false });
  }, [searchParams, setSearchParams]);

  const onTimelineFocus = useCallback(
    (target: { kind: 'run' | 'workItem' | 'rfi'; id: string }) => {
      if (target.kind === 'workItem') navigate(`/workbench?item=${encodeURIComponent(target.id)}`);
      else if (target.kind === 'rfi') setTab('rfis');
      else navigate('/runs');
    },
    [navigate],
  );

  const displayName = entity?.displayName ?? entity?.typeKey ?? entityId;
  const riskBand = entity?.riskBand ?? (entity?.attrs?.riskBand as string | undefined);

  // Whole entity not live → teach and stop (the other sections have nothing to hang on).
  if (entityUnavailable && !entity) {
    return (
      <div className="ol-page">
        <EmptyState
          icon={<DeploymentUnitOutlined />}
          title="Entity-360"
          wave="PS-3"
          description="The bitemporal entity view — attributes, ownership, timeline and evidence, all readable as of any past instant — renders here once the entity API is live for this hub."
          bullets={[
            'Point-in-time ("as of date X") reads of what was known and when',
            'UBO ownership traversal with effective fractions',
            'A newest-first timeline of graph changes, runs, decisions, signals and RFIs',
            'Evidence with validity windows and provenance',
          ]}
          action={<Button onClick={() => navigate('/workbench')}>Back to workbench</Button>}
        />
      </div>
    );
  }

  return (
    <div className="ol-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
            <DeploymentUnitOutlined style={{ marginRight: 10, color: 'var(--ol-primary)' }} />
            {displayName}
          </Title>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Text className="ol-mono" style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)' }}>
              {entityId}
            </Text>
            {entity?.typeKey && <Tag bordered={false}>{entity.typeKey}</Tag>}
            <EntityStatusChip status={entity?.status} />
            <RiskChip band={riskBand} />
          </div>
        </div>
        <Button icon={<InboxOutlined />} onClick={() => navigate(`/workbench?entityId=${encodeURIComponent(entityId)}`)}>
          Work items
        </Button>
      </div>

      {/* As-of point-in-time control */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          margin: '14px 0 6px',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          <FieldTimeOutlined /> As of
        </Text>
        <input
          type="datetime-local"
          aria-label="Point-in-time coordinate (as of)"
          value={pending}
          onChange={(e) => setPending(e.target.value)}
          style={{
            background: 'var(--ol-bg-container)',
            color: 'var(--ol-text-primary)',
            border: '1px solid var(--ol-border)',
            borderRadius: 6,
            padding: '5px 8px',
            fontSize: 12.5,
            colorScheme: 'dark light',
          }}
        />
        <Button size="small" icon={<HistoryOutlined />} onClick={applyAsOf} disabled={!pending}>
          View as of
        </Button>
        {asOf && (
          <Button size="small" icon={<RollbackOutlined />} onClick={returnToNow}>
            Return to now
          </Button>
        )}
      </div>

      {/* Persistent PIT banner */}
      {asOf && (
        <div className="ol-pit-banner" role="status" style={{ marginBottom: 12 }}>
          <span className="ol-pit-banner__dot" aria-hidden />
          <span>
            Viewing as of <strong>{formatAbsolute(asOf)}</strong> — the entity as we knew it then.
            Everything below (attributes, ownership, evidence, timeline) is read at this coordinate.
          </span>
          <Button size="small" type="link" onClick={returnToNow} style={{ marginInlineStart: 'auto' }}>
            Return to now
          </Button>
        </div>
      )}

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <div className="ol-section">Attributes</div>
                  {entity && Object.keys(entity.attrs ?? {}).length > 0 ? (
                    <div className="ol-card">
                      <div className="ol-kv">
                        {Object.entries(entity.attrs).map(([k, v]) => (
                          <div style={{ display: 'contents' }} key={k}>
                            <span className="ol-kv__k">{k}</span>
                            <span className="ol-kv__v">
                              {attrValue(v)}
                              {erasedAttrClass(v) && (
                                <Tag bordered={false} style={{ marginInlineStart: 8 }} data-testid="erased-class">
                                  {erasedAttrClass(v)}
                                </Tag>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loadingCore ? 'Loading…' : 'No attributes.'} />
                  )}
                </div>

                {entity && entity.edges && entity.edges.length > 0 && (
                  <div>
                    <div className="ol-section">
                      <ApartmentOutlined /> Relationships
                      <span className="ol-section__count">· {entity.edges.length}</span>
                    </div>
                    <Space size={[6, 6]} wrap>
                      {entity.edges.map((e, i) => (
                        <Tag
                          key={`${e.entityId}-${i}`}
                          bordered={false}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/entities/${encodeURIComponent(e.entityId)}`)}
                          title={`Open ${e.entityId}`}
                        >
                          {e.role ? `${e.role}: ` : ''}
                          {e.displayName ?? e.typeKey ?? e.entityId}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}

                <div>
                  <div className="ol-section">
                    <ClockCircleOutlined /> Lifecycle clocks
                  </div>
                  <ClocksPanel clocks={entity?.clocks ?? []} now={now} />
                </div>
              </div>
            ),
          },
          {
            key: 'ownership',
            label: 'Ownership (UBO)',
            children: uboUnavailable ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ownership traversal is not live for this hub yet." />
            ) : (
              <div style={{ maxWidth: 640 }}>
                <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 0 }}>
                  Effective ownership through ownership-edge chains (≤ 4 hops). FATF UBO ≥ 25% and
                  OFAC control ≥ 50% are flagged.
                </Paragraph>
                <UboTree ubo={ubo} />
              </div>
            ),
          },
          {
            key: 'timeline',
            label: 'Timeline',
            children: timelineUnavailable ? (
              <EmptyState
                icon={<HistoryOutlined />}
                title="Entity timeline"
                wave="PS-3"
                description="A newest-first ledger of everything that touched this entity — graph changes, runs, decisions, signals, clock events and RFIs — filterable by kind and readable as of any instant."
                bullets={[
                  'Per-kind glyphs and colours; click an event to focus its run / work-item / RFI',
                  'Kind filter chips to isolate a signal thread or a decision trail',
                  'Honours the as-of coordinate — see the ledger as it stood then',
                ]}
              />
            ) : (
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12, alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 11, marginRight: 4 }}>
                    Kinds
                  </Text>
                  <CheckableTag checked={kinds.length === 0} onChange={() => setKinds([])}>
                    All
                  </CheckableTag>
                  {TIMELINE_KINDS.map((k) => (
                    <CheckableTag
                      key={k}
                      checked={kinds.includes(k)}
                      onChange={(c) =>
                        setKinds((prev) => (c ? [...prev, k] : prev.filter((x) => x !== k)))
                      }
                    >
                      {k}
                    </CheckableTag>
                  ))}
                </div>
                <EntityTimeline events={timeline} now={now} onFocus={onTimelineFocus} />
              </div>
            ),
          },
          {
            key: 'evidence',
            label: 'Evidence',
            children: evidenceUnavailable ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="The evidence endpoint is not live for this hub yet." />
            ) : (
              <div style={{ maxWidth: 640 }}>
                <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 0 }}>
                  Evidence bound to this entity, with validity windows shown{' '}
                  {asOf ? 'as of the selected instant' : 'as of now'} and provenance.
                </Paragraph>
                <EvidenceList evidence={evidence} atMs={asOf ? Date.parse(asOf) : now} />
              </div>
            ),
          },
          {
            key: 'rfis',
            label: (
              <span>
                <MailOutlined /> RFIs
              </span>
            ),
            children: <RfiListPanel entityId={entityId} />,
          },
        ]}
      />
    </div>
  );
}
